import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import client from '../../../lib/feathers';

export default function AvaConversationDetail() {
  const { sessionId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedContext, setExpandedContext] = useState({});

  const getConversation = async () => {
    try {
      setLoading(true);
      const service = client.service('admin/ava-conversations');
      const result = await service.get(sessionId);
      setConversation(result);
    } catch (e) {
      console.log('error', e);
      alert('There was an issue loading the conversation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getConversation();
  }, [sessionId]);

  const toggleContext = (id) => {
    setExpandedContext((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <Page>
        <div className="text-center py-12 text-gray-500">Loading conversation...</div>
      </Page>
    );
  }

  if (!conversation) {
    return (
      <Page>
        <div className="text-center py-12 text-gray-500">Conversation not found.</div>
      </Page>
    );
  }

  const { user, messages, totalTokensIn, totalTokensOut } = conversation;

  return (
    <Page>
      <Breadcrumb elements={[
        { name: 'Ava Conversations', href: '/admin/ava-conversations' },
        { name: `${user.firstName} ${user.lastName}` }
      ]} />

      <div className="mt-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {user.firstName} {user.lastName}
          </h1>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>
        <div className="mt-3 flex gap-4 text-sm text-gray-600">
          <span className="bg-gray-100 px-3 py-1 rounded-full">
            {messages.length} messages
          </span>
          <span className="bg-blue-50 px-3 py-1 rounded-full">
            Tokens in: {totalTokensIn.toLocaleString()}
          </span>
          <span className="bg-green-50 px-3 py-1 rounded-full">
            Tokens out: {totalTokensOut.toLocaleString()}
          </span>
        </div>
      </div>

      <div className="space-y-4 max-w-4xl">
        {messages.map((msg) => (
          <div key={msg.id}>
            <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-2xl rounded-lg px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-blue-100 text-gray-900'
                  : 'bg-white border border-gray-200 text-gray-900'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold uppercase ${
                    msg.role === 'user' ? 'text-blue-700' : 'text-purple-700'
                  }`}>
                    {msg.role === 'user' ? 'User' : 'Ava'}
                  </span>
                  <span className="text-xs text-gray-400">
                    {format(new Date(msg.createdAt), 'MMM d, yyyy h:mm a')}
                  </span>
                </div>
                <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                {(msg.tokensIn || msg.tokensOut) && (
                  <div className="mt-2 text-xs text-gray-400">
                    {msg.tokensIn ? `In: ${msg.tokensIn.toLocaleString()}` : ''}
                    {msg.tokensIn && msg.tokensOut ? ' · ' : ''}
                    {msg.tokensOut ? `Out: ${msg.tokensOut.toLocaleString()}` : ''}
                  </div>
                )}
              </div>
            </div>
            {msg.contextUsed && (
              <div className={`mt-1 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                <button
                  onClick={() => toggleContext(msg.id)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  {expandedContext[msg.id] ? '▼ Hide context' : '▶ Show context used'}
                </button>
                {expandedContext[msg.id] && (
                  <div className="mt-1 max-w-2xl inline-block text-left">
                    <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-600 overflow-x-auto">
                      {JSON.stringify(msg.contextUsed, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Page>
  );
}
